// @refresh reset — dev-only: force remount so the useMemo([]) board nodes hot-reload on edit (no prod effect)
import { useMemo } from "react";
import { ReactFlow, type Node, type NodeTypes, Background, BackgroundVariant } from "@xyflow/react";
import "@xyflow/react/dist/style.css";

// ─────────────────────────────────────────────────────────────────────────────
// MAX Security — internal discovery board (thinking canvas, not a client doc).
// Consolidated layout (2026-07-15): every category is ONE bordered frame, with its
// cards packed in a multi-column grid INSIDE it — full-width, tight, no long column.
// The whole board is a single self-laying-out node (CSS flex + grid) so nothing
// overlaps and the width is used. Content grounded in kb-doc/max-security/discovery/*
// and the 2026-07 isolation + pricing research sweep.
// ─────────────────────────────────────────────────────────────────────────────

const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const T = {
  ink: "#1C2021",
  ink2: "#57534E",
  line: "#EADDD0",
  frame: "#FBF7F2",
  white: "#FFFFFF",
  petrol: "#2B5560",
  petrolBg: "rgba(43,85,96,0.10)",
  crit: "#B85638",
  high: "#C98A3C",
  med: "#7E8A86",
};

const SEV: Record<string, string> = { Critical: T.crit, High: T.high, Medium: T.med, Constraint: T.petrol };

function box(highlight?: boolean): React.CSSProperties {
  return {
    background: T.white,
    border: highlight ? `2px solid ${T.petrol}` : `1px solid ${T.line}`,
    borderRadius: 14,
    padding: 20,
    display: "flex",
    flexDirection: "column",
    width: "100%",
  };
}
function Label({ children }: { children: any }) {
  return (
    <span style={{ display: "inline-block", fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: T.petrol, background: T.petrolBg, borderRadius: 999, padding: "4px 10px", marginBottom: 11, alignSelf: "flex-start" }}>
      {children}
    </span>
  );
}

// ── the five box kinds (rendered inside a category frame's grid) ────────────
function CardBox({ d }: { d: any }) {
  return (
    <div style={box(d.highlight)}>
      {d.label && <Label>{d.label}</Label>}
      <div style={{ fontSize: 19, fontWeight: 800, color: T.ink, letterSpacing: "-0.02em", lineHeight: 1.18 }}>{d.title}</div>
      {d.lede && <div style={{ marginTop: 9, fontSize: 13.5, color: T.ink2, lineHeight: 1.5 }}>{d.lede}</div>}
      {d.rows && (
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 9 }}>
          {d.rows.map((r: any, i: number) => (
            <div key={i} style={{ display: "flex", gap: 12, alignItems: "baseline" }}>
              <span style={{ flexShrink: 0, width: 108, fontSize: 11.5, fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase", color: T.ink }}>{r.k}</span>
              <span style={{ fontSize: 13, color: T.ink2, lineHeight: 1.4 }}>{r.v}</span>
            </div>
          ))}
        </div>
      )}
      {d.bullets && (
        <ul style={{ marginTop: 12, paddingLeft: 17, display: "flex", flexDirection: "column", gap: 7 }}>
          {d.bullets.map((b: string, i: number) => (
            <li key={i} style={{ fontSize: 13, color: T.ink2, lineHeight: 1.45 }}>{b}</li>
          ))}
        </ul>
      )}
      {d.href && (
        <a href={d.href} className="nopan nodrag" style={{ marginTop: 14, alignSelf: "flex-start", padding: "9px 15px", borderRadius: 999, background: T.petrol, color: T.white, fontSize: 13.5, fontWeight: 700, textDecoration: "none", pointerEvents: "auto" }}>
          {d.cta || "Open"} →
        </a>
      )}
    </div>
  );
}
function StripBox({ d }: { d: any }) {
  return (
    <div style={box()}>
      {d.label && <Label>{d.label}</Label>}
      <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
        {d.steps.map((s: string, i: number) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ fontSize: 12, fontWeight: 600, padding: "5px 10px", borderRadius: 999, whiteSpace: "nowrap", color: T.ink2, background: T.frame, border: `1px solid ${T.line}` }}>{i + 1}. {s}</span>
            {i < d.steps.length - 1 && <span style={{ color: T.med, fontSize: 13 }}>→</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
function PainBox({ d }: { d: any }) {
  return (
    <div style={box()}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12, gap: 8 }}>
        <Label>{d.label}</Label>
        {d.footnote && <span style={{ fontSize: 11.5, color: T.med, fontWeight: 500, textAlign: "right" }}>{d.footnote}</span>}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {d.pains.map((p: any, i: number) => (
          <div key={i} style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
            <span style={{ flexShrink: 0, width: 8, height: 8, borderRadius: 999, background: SEV[p.sev] || T.med, transform: "translateY(-1px)" }} />
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ fontSize: 13, color: T.ink, lineHeight: 1.35, fontWeight: 500 }}>{p.text}</span>
              {p.type && <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase", color: T.med }}>{p.sev} · {p.type}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
function QuoteBox({ d }: { d: any }) {
  return (
    <div style={{ ...box(), background: T.frame }}>
      <div style={{ fontSize: 15, fontWeight: 500, color: T.ink, lineHeight: 1.45, fontStyle: "italic" }}>“{d.quote}”</div>
      <div style={{ marginTop: 10, fontSize: 12, fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase", color: T.petrol }}>— {d.who}</div>
    </div>
  );
}
function TableBox({ d }: { d: any }) {
  const cols: string[] = d.columns || [];
  const rows: string[][] = d.rows || [];
  const hi = new Set<number>(d.highlightRows || []);
  return (
    <div style={box()}>
      {d.label && <Label>{d.label}</Label>}
      {d.title && <div style={{ fontSize: 18, fontWeight: 800, color: T.ink, letterSpacing: "-0.02em", marginBottom: 14 }}>{d.title}</div>}
      <div style={{ display: "grid", gridTemplateColumns: d.grid || `repeat(${cols.length}, 1fr)` }}>
        {cols.map((c, i) => (
          <div key={"h" + i} style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.02em", textTransform: "uppercase", color: T.ink, padding: "0 12px 10px 0", borderBottom: `2px solid ${T.petrol}` }}>{c}</div>
        ))}
        {rows.map((r, ri) =>
          r.map((cell, ci) => (
            <div key={ri + "-" + ci} style={{ fontSize: 12.5, color: ci === 0 ? T.ink : T.ink2, fontWeight: ci === 0 ? 700 : 500, lineHeight: 1.35, padding: "10px 12px 10px 0", borderBottom: `1px solid ${T.line}`, background: hi.has(ri) ? T.petrolBg : "transparent" }}>{cell}</div>
          )),
        )}
      </div>
      {d.footnote && <div style={{ marginTop: 12, fontSize: 11.5, color: T.med, lineHeight: 1.5 }}>{d.footnote}</div>}
    </div>
  );
}

function Cell({ item }: { item: any }) {
  const inner =
    item.kind === "strip" ? <StripBox d={item} /> :
    item.kind === "pain" ? <PainBox d={item} /> :
    item.kind === "quote" ? <QuoteBox d={item} /> :
    item.kind === "table" ? <TableBox d={item} /> :
    <CardBox d={item} />;
  return <div style={{ gridColumn: `span ${item.span || 1}`, display: "flex" }}>{inner}</div>;
}

// ── a category FRAME: title + a grid of boxes inside one bordered container ──
function GroupFrame({ g }: { g: any }) {
  return (
    <div style={{ background: T.frame, border: `1px solid ${T.line}`, borderRadius: 24, padding: 30, boxShadow: "0 20px 50px rgba(0,0,0,0.08)" }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 34, fontWeight: 800, color: T.ink, letterSpacing: "-0.025em", lineHeight: 1.05 }}>{g.title}</div>
        {g.sub && <div style={{ marginTop: 6, fontSize: 15.5, fontWeight: 500, color: T.ink2 }}>{g.sub}</div>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${g.cols}, 1fr)`, gap: 16, alignItems: "start" }}>
        {g.items.map((it: any, i: number) => <Cell key={i} item={it} />)}
      </div>
    </div>
  );
}

export function BoardNode({ data }: { data: any }) {
  return (
    <div style={{ fontFamily: FONT, width: data.width, display: "flex", flexDirection: "column", gap: 26 }}>
      {data.groups.map((g: any, i: number) => <GroupFrame key={i} g={g} />)}
    </div>
  );
}

const nodeTypes: NodeTypes = { board: BoardNode };

// ─────────────────────────────────────────────────────────────────────────────
// CONTENT — one entry per category frame. cols = grid columns; items carry a
// `kind` (card | strip | pain | quote | table) and optional `span`.
// ─────────────────────────────────────────────────────────────────────────────
export const GROUPS = [
  {
    title: "MAX Security",
    sub: "the engagement · where we are",
    cols: 4,
    items: [
      { kind: "strip", span: 4, label: "Engagement arc", steps: ["Intro", "Proposal", "Kickoff", "Discovery", "Analysis review", "Pilot decision"] },
      { kind: "card", span: 4, highlight: true, label: "The client deck", title: "Discovery Findings & the Way Forward", lede: "This whole story, built as the client-facing pitch deck for Eyal and Tzahi — the four departments' pains, the one root cause beneath them, the solution, and every technical option for how we build it and where it runs.", href: "/pitch-doc/max-security/discovery-findings", cta: "Open the deck" },
      { kind: "card", label: "Who they are", title: "A 250-person Israeli intelligence firm", lede: "Founded 1996, Tel Aviv. ~150+ analysts, 160+ countries, bootstrapped. Already runs its own RAG product, Scout AI, on AWS Bedrock — but has zero in-house engineering. Champion: Eyal Ben-Shaul (Partner & CEO, North America)." },
      { kind: "card", label: "The pilot", title: "Discover → pick 2–3 automations → build", lede: "An AI-transformation pilot: map the departments, choose the highest-ROI automations, build + deploy them on infrastructure MAX owns, and teach them the cycle. Discovery ran May–June 2026 across four departments." },
      { kind: "card", highlight: true, label: "Where we are", title: "At the pilot-selection decision point", lede: "All four departments mapped and certified. No live pilot yet. Emerging top-3: Lead Qualification & Enrichment · Client Snapshot · Operations Single Brain." },
      { kind: "card", label: "The reframe", title: "“Our pain is in the pain-world”", lede: "In the 06-23 review, Tzahi Shraga (Chief Intelligence Officer) rejected the sales-automation menu: it answered the real pain — data fragmentation, no organizational memory — only “very partially.”" },
    ],
  },
  {
    title: "Discovery — by department",
    sub: "each department's process, the pains they named, and their own words",
    cols: 4,
    items: [
      { kind: "strip", label: "Sales · process", steps: ["Lead", "Allocate", "Qualify", "Outreach", "Meeting", "Proposal", "Loop", "Close"] },
      { kind: "strip", label: "Marketing · process", steps: ["Setup", "Event", "Brief", "Produce", "Review", "Distribute", "Capture", "Hand-off"] },
      { kind: "strip", label: "Operations · process", steps: ["Intake", "Risk profile", "Plan", "Price", "Approve", "GSOC", "Mission", "Close"] },
      { kind: "strip", label: "Consulting · process", steps: ["Entry", "Research", "Survey", "Risk-matrix", "Gaps", "Report", "Deliver"] },
      { kind: "pain", label: "Sales (Ori Mor, CRO)", footnote: "+4 · 11 systems, none authoritative", pains: [
        { sev: "Critical", type: "Data-fragmentation", text: "No single current picture across systems" },
        { sev: "Critical", type: "Capacity", text: "~Half of 45,000 contacts sit unworked" },
        { sev: "Critical", type: "Manual", text: "Account-by-account outreach grind" },
        { sev: "Critical", type: "Disconnect", text: "WhatsApp is gold, connected to nothing" },
        { sev: "High", type: "Manual", text: "Firefly notes copy-pasted into HubSpot by hand" },
      ] },
      { kind: "pain", label: "Marketing (Odeia)", footnote: "+7 · webinars ≈ 90% of demand-gen", pains: [
        { sev: "Critical", type: "Process-gap", text: "No MQL layer — leads dumped straight to sales" },
        { sev: "Critical", type: "Disconnect", text: "HubSpot ↔ Monday never sync" },
        { sev: "High", type: "Process-gap", text: "No segmentation — region-only email blasts" },
        { sev: "High", type: "Knowledge-loss", text: "30-day backlog of thousands of idle leads" },
        { sev: "High", type: "No-attribution", text: "Lead source & journey are lost" },
      ] },
      { kind: "pain", label: "Operations (Eyal Ofer + team)", footnote: "+11 · the deepest department", pains: [
        { sev: "Critical", type: "Fragmentation", text: "No live consolidated picture — never one view" },
        { sev: "Critical", type: "Error-risk", text: "Commercials priced by hand by non-numbers people" },
        { sev: "Critical", type: "Knowledge-loss", text: "No org memory — history lost across four silos" },
        { sev: "Critical", type: "Manual", text: "Collecting & re-validating client info — #1 time sink" },
        { sev: "Constraint", type: "On-prem only", text: "External AI on ops data = “a huge violation”" },
      ] },
      { kind: "pain", label: "Consulting (2-person advisory)", footnote: "+2 · thinnest dept, likely out of top-3", pains: [
        { sev: "Critical", type: "Manual", text: "Risk report rebuilt by hand — days each, all similar" },
        { sev: "High", type: "Capacity", text: "Two-person ceiling — all reports funnel through the lead" },
        { sev: "High", type: "Knowledge-loss", text: "Benchmarks live in the team's own DB" },
        { sev: "High", type: "Process-gap", text: "Site-specific judgment doesn't templatize" },
      ] },
      { kind: "quote", quote: "I have no capacity — I could hire five more salespeople just to work this.", who: "Ori Mor, CRO" },
      { kind: "quote", quote: "An email that goes out goes to everyone — completely un-segmented, purely region-based.", who: "Odeia Hartavi" },
      { kind: "quote", quote: "A client we supported three years ago — now only two people remember. History fragmented across four places.", who: "Operations senior mgmt" },
      { kind: "quote", quote: "The report takes several good days, and every report is quite similar. AI is critical for me.", who: "Consulting lead" },
    ],
  },
  {
    title: "The shared problem → the root cause",
    sub: "the same fault line under every department — it lives at the seams",
    cols: 4,
    items: [
      { kind: "card", label: "Doesn't connect", title: "The systems don't talk", bullets: ["HubSpot ↔ Monday never sync", "Scout portal read by hand, wired to nothing", "Firefly writes to Notes, not the log", "SAP disconnected; WhatsApp trapped"] },
      { kind: "card", label: "Doesn't persist", title: "No organizational memory", bullets: ["Plans, quotes, history rebuilt every time", "Same client is “Aflam” / “Aflame” / a legal name", "Attribution loop never closes"] },
      { kind: "card", label: "Arrives dirty", title: "The marketing → sales seam leaks", bullets: ["~45,000 contacts, half owned by no one", "“Qualified” by a corporate-email check", "No scoring, no enrichment, no MQL"] },
      { kind: "card", highlight: true, label: "One entity, four names", title: "Lead → account → client → mission", lede: "The same entity travels across Marketing, Sales, and Operations — but each hop is a different system and the record doesn't carry across. The pipeline works; the data about the entity doesn't." },
      { kind: "card", span: 4, highlight: true, label: "The root cause — one sentence", title: "No connected, persistent, trustworthy record of an entity across its lifecycle", lede: "MAX runs one revenue-to-delivery pipeline — Marketing generates demand, Sales converts, Operations delivers. The pipeline works. What fails is that the DATA about the entity doesn't travel cleanly across the seams and doesn't accumulate into memory." },
      { kind: "card", label: "Mechanism A", title: "Doesn't CONNECT", lede: "No single source of truth. MAX's own stated #1 pain." },
      { kind: "card", label: "Mechanism B", title: "Doesn't PERSIST", lede: "No org memory — every cycle rebuilds what the last knew." },
      { kind: "card", label: "Mechanism C", title: "Arrives DIRTY", lede: "Un-enriched intake — no scoring, no MQL at capture." },
      { kind: "card", label: "Sequencing note", title: "One “pain” is culture, not data", lede: "The Ops time-visibility gap is compliance/culture — defer measurement; it is not the first automation." },
    ],
  },
  {
    title: "The solution",
    sub: "build the entity record first — then everything else is a feature on top",
    cols: 4,
    items: [
      { kind: "card", highlight: true, label: "Start here", title: "Client Snapshot", lede: "Assemble one current picture of an account across HubSpot + Monday + Firefly + Outlook, and enrich every new lead at intake. The root-cause fix — and it turns the “garbage-in” objection into the value prop." },
      { kind: "card", label: "Then", title: "Single Brain", lede: "An index + summaries over all systems (“stronger than a vector DB” — Tzahi's framing). Reconstruct org memory by running history backwards from past invoices and prices." },
      { kind: "card", highlight: true, label: "What they actually want", title: "Background, not collaborative", lede: "Collaborative = a human invokes a skill now. Background = autonomous, at scale, needs secure infra. MAX wants real automations running in the background — a bigger build than the original 2–3." },
      { kind: "card", label: "Hard boundary", title: "Commercial external · mission on-prem", lede: "HubSpot/Monday/Firefly/Outlook can use external models — the first proof. Anything touching the plan, Scout, or mission docs runs on-prem." },
      { kind: "card", span: 2, label: "Emerging top-3", title: "Where the pilot lands", rows: [{ k: "1", v: "Lead Qualification & Enrichment — the marketing→sales seam" }, { k: "2", v: "Client Snapshot — the connected entity record" }, { k: "3", v: "Operations Single Brain — org-memory from history" }] },
      { kind: "card", label: "Commercial model", title: "They pay Anthropic directly", lede: "MAX holds its own enterprise account and pays the model bill directly — no markup. MyJarvis keeps that bill small. Two budgets, two decisions." },
      { kind: "card", label: "The open decision", title: "Same price, or ask for more?", lede: "Background automations are bigger than the 2–3 scoped. Keep the pilot price or re-scope. A happy client is the win." },
    ],
  },
  {
    title: "Where it runs — complete isolation",
    sub: "their real blocker: can AI run so client data provably never leaves? — the honest ladder",
    cols: 4,
    items: [
      { kind: "card", span: 2, highlight: true, label: "The client's bar", title: "“We run our own inference — data can't leak anywhere”", lede: "MAX's deepest blocker isn't ROI — it's letting AI near intelligence data at all. What they really want: provably-our-own inference, no third party's hardware anywhere in the path. This is whether that's real, and how you'd build it." },
      { kind: "card", span: 2, label: "The core truth", title: "“Complete isolation” is a ladder, not a checkbox", lede: "Every option is a point on one spectrum: how much you trust someone else's silicon — from a contract (Bedrock), to cryptography (confidential computing), to physics (air-gap). Only the top rung removes everyone else." },
      { kind: "card", label: "Rung 1 · weakest", title: "Managed API — Bedrock / Azure / Vertex", lede: "Data stays in your account, no training, private path (PrivateLink). But inference runs on the provider's shared, provider-owned GPUs.", rows: [{ k: "Reality", v: "Not your own inference" }] },
      { kind: "card", label: "Rung 2 · middle", title: "Confidential computing", lede: "Nitro Enclaves + NVIDIA H100 TEE with remote attestation. Cryptographically defeats rogue-admin & co-tenant threats — verify before releasing data.", rows: [{ k: "Reality", v: "Still their silicon" }] },
      { kind: "card", label: "Rung 3 · strong", title: "Self-host in your own VPC", lede: "Your dedicated GPU, private subnet, no internet egress. You run the weights — but it sits on AWS's physical substrate.", rows: [{ k: "Reality", v: "Your box, cloud floor" }] },
      { kind: "card", highlight: true, label: "Rung 4 · absolute", title: "On-prem / air-gapped", lede: "Your own GPUs behind your firewall — no egress path exists by design. The only physics-level “100% certain,” and the only provably-your-own inference.", rows: [{ k: "Reality", v: "The real answer" }] },
      { kind: "card", span: 2, label: "The myth-bust", title: "Bedrock is NOT “your own inference”", bullets: ["Inference runs in AWS's Model Deployment Account — outside your account", "Provisioned Throughput = reserved throughput, NOT dedicated silicon", "PrivateLink privatizes the network path; the GPUs still live on AWS's side", "Azure OpenAI + Google Vertex are the exact same posture"] },
      { kind: "card", highlight: true, label: "The hard tradeoff", title: "Self-host = no Claude", lede: "Anthropic never licenses Claude weights — Claude is managed-only. “Provably our own inference” and “we want Claude” cannot both be true. Full isolation means an open-weight model." },
      { kind: "card", label: "2026 Bedrock traps", title: "Two landmines to flag", bullets: ["Newest Claude “covered models” force prompt-sharing (30-day) → pin to standard Opus/Sonnet/Haiku", "Global endpoint = no residency guarantee; can't pin Claude inference to Israel"] },
      { kind: "card", span: 2, highlight: true, label: "The honest recommendation", title: "Tiered by data sensitivity", lede: "Managed-in-VPC is cheaper AND higher quality, so self-host ONLY where the mandate forbids even that. Commercial data (HubSpot/Monday/Outlook) → Claude on their Bedrock, the first proof. Mission/intel/Scout data → a self-hosted open-weight model on-prem. One architecture, engine swappable per data-class." },
      { kind: "card", highlight: true, label: "The question for Erez", title: "How hard is the air-gap mandate?", lede: "Does a zero-retention, no-training, in-THEIR-VPC agreement satisfy legal — or is it a literal physics-level air-gap? First → hybrid with Claude on Bedrock. Second → open model self-hosted. Ask before we scope." },
      { kind: "card", label: "Caveat", title: "This market moves monthly", lede: "Region availability, model IDs, licenses and leaderboard leaders shift fast. Re-verify before a client proposal." },
    ],
  },
  {
    title: "The models — the open-source menu",
    sub: "if we self-host, these are the weights we'd run — non-Chinese, ranked by performance",
    cols: 4,
    items: [
      { kind: "card", span: 2, highlight: true, label: "You asked about NVIDIA — the three tiers", title: "NVIDIA Nemotron · Nano / Super / Ultra", lede: "All commercially clean (NVIDIA Open License), shipping as air-gapped NIM containers. Gen-2 Nemotron-3 adds a 550B Ultra + 1M context.", rows: [{ k: "Nano ~9B", v: "1 GPU · small/edge · high-throughput" }, { k: "Super 49B", v: "1× H100 · strong-mid · the sweet spot" }, { k: "Ultra 253B", v: "8× H100 node · frontier · GPQA 76" }] },
      { kind: "card", span: 2, label: "License landmines", title: "Which weights are free to self-host", bullets: ["Clean for commercial self-host: gpt-oss, Llama 3.x/4, Nemotron (all tiers), Mistral Small / Mixtral, Gemma, Phi, IBM Granite, OLMo", "Paid license required: Cohere Command (incl. the Hebrew one), Mistral Large 2 / 3, Codestral (non-production)", "Serving: NVIDIA NIM (supported, air-gap-documented) or open vLLM (free, you run ops)"] },
      { kind: "table", span: 4, label: "The model menu (non-Chinese)", title: "Open-weight models, ranked by performance",
        columns: ["Model", "Params (total / active)", "GPU to serve", "License — commercial self-host?", "Perf tier"],
        grid: "1.5fr 1.2fr 1.15fr 1.55fr 1.1fr",
        rows: [
          ["Nemotron Ultra 253B", "253B dense", "8× H100 (FP8)", "NVIDIA Open — yes", "Frontier · GPQA 76"],
          ["Nemotron-3 Ultra 550B", "550B / 55B MoE", "8× H100 / 4× H200", "NVIDIA Open — yes", "Frontier (new)"],
          ["Llama 4 Maverick", "400B / 17B MoE", "8× H100 (FP8)", "Llama 4 — yes (<700M MAU)", "Frontier-adjacent"],
          ["Llama 3.1 405B", "405B dense", "8× H100 (FP8)", "Llama 3.1 — yes", "Frontier-adjacent"],
          ["Mistral Large 3", "675B / 41B MoE", "~8× H100", "PAID (Mistral MRL)", "Frontier"],
          ["gpt-oss-120b", "117B / 5.1B MoE", "1× H100 80GB", "Apache-2.0 — yes", "Strong → frontier-adj."],
          ["Cohere Command A", "111B dense", "2× H100", "PAID (Cohere) · Hebrew", "Strong → frontier-adj."],
          ["Nemotron Super 49B v1.5", "49B dense", "1× H100 80GB", "NVIDIA Open — yes", "Strong mid"],
          ["Llama 3.3 70B", "70B dense", "2× H100 (1× FP8)", "Llama 3.3 — yes", "Strong mid"],
          ["Llama 4 Scout", "109B / 17B MoE", "1× H100 (int4)", "Llama 4 — yes", "Strong mid · MM"],
          ["Mixtral 8×22B", "141B / 39B MoE", "4× H100", "Apache-2.0 — yes", "Strong mid (aging)"],
          ["Mistral Small 3.1", "24B dense", "1× H100 80GB", "Apache-2.0 — yes", "Strong small · MM"],
          ["Gemma 3 27B", "27B dense", "1× H100 80GB", "Gemma — yes", "Strong small · MM"],
          ["Phi-4", "14.7B dense", "1× GPU (~30GB)", "MIT — yes", "Small (≈70B-class)"],
          ["gpt-oss-20b", "21B / 3.6B MoE", "1× 16GB GPU", "Apache-2.0 — yes", "Small (≈o3-mini)"],
          ["Nemotron Nano 9B", "9B hybrid", "1× GPU (~20GB)", "NVIDIA Open — yes", "Small / edge"],
        ],
        highlightRows: [0, 1, 5, 7, 15],
        footnote: "Chinese-origin models (Qwen, DeepSeek, GLM) are EXCLUDED — they top the open leaderboards but are disqualified on provenance for an intelligence client. “PAID” = weights downloadable for evaluation, but production self-host needs a paid license. MM = multimodal. Highlighted = the NVIDIA Nemotron family + gpt-oss-120b (the standout single-GPU value)." },
    ],
  },
  {
    title: "The price — buy vs rent",
    sub: "pick a model tier, read across to the box and the monthly cost — all inside your walls",
    cols: 4,
    items: [
      { kind: "card", span: 4, highlight: true, label: "How to read this", title: "Options, not a recommendation", lede: "Two independent choices. (1) WHICH model — a performance tier from the menu above. (2) WHICH box — buy it (a true air-gap) or rent dedicated (single-tenant, not air-gapped). A model's GPU need maps straight to a hardware row and its monthly cost. Everything runs the model, the agent, AND the knowledge base fully inside your walls." },
      { kind: "table", span: 4, label: "The money table", title: "Hardware tiers — what it runs, what it costs, what it serves",
        columns: ["Config", "Runs (examples)", "Buy", "Own · $/mo", "Rent · $/mo", "Capacity", "$/M-tok"],
        grid: "1.05fr 2.1fr 0.9fr 1fr 1.05fr 1.55fr 0.95fr",
        rows: [
          ["1× H100 80GB", "gpt-oss 20B/120B · Mistral 24B · Gemma 27B · Phi-4", "~$40–50K", "~$1.9–2.4K", "~$2–9K", "dozens of users", "~$0.30–0.50"],
          ["2× H100", "Llama 70B · Nemotron Super 49B · Command A", "~$90K", "~$3.1–3.8K", "~$4–18K", "~200–250 concurrent · ~11B tok/mo", "$0.35 → $1.40"],
          ["4× H100", "Llama 70B (snappy) · Mixtral 8×22B", "~$150–190K", "~$5–6K", "~$8–35K", "250 users @ 34 tok/s", "—"],
          ["8× H100 node", "Nemotron Ultra 253B · Llama 405B / Maverick", "~$250–400K", "~$10.4–13.2K", "~$15.7–72K", "~150–250 users (frontier)", "~$1.15–1.40"],
          ["8× H200 node", "Frontier + long-context headroom", "~$400–500K", "~$14–17.4K", "~$25–80K", "~200 users (frontier)", "~$1.15–1.40"],
        ],
        highlightRows: [1],
        footnote: "Own $/mo = 3-yr amortized hardware + power & cooling (PUE 1.5 @ $0.135/kWh). Add ~$375/GPU/mo ONLY if running NVIDIA NIM — $0 with open vLLM, bundled free on DGX. Rent = dedicated single-tenant, 24/7, cheap-neocloud → hyperscaler. $/M-token at high utilization (rises sharply when idle)." },
      { kind: "card", span: 2, highlight: true, label: "Buy the box → run it unlimited", title: "Once it's yours, the marginal cost is electricity", lede: "A 2× H100 box (~$90K) serves the WHOLE 250-person firm — 200–250 concurrent, you'll realistically have 30–80. At full tilt ~11 billion tokens/month, ~40–60M per employee. The more you run it, the cheaper each token — asymptoting to a ~$0.35/M electricity floor. Utilization is the only number that matters." },
      { kind: "card", span: 2, label: "Rent vs buy", title: "Rent is fast; only buy is a true air-gap", lede: "Rent = no capex, ~$4–18K/mo for a 70B box, live in days — but a rented instance is single-tenant inside someone's cloud, NOT an air-gap. Buy = capex, but the only path with no third-party physical custody. Break-even: buying beats hyperscaler-rent in ~6–9 months, cheap-neocloud in ~18–30 months; buy wins above ~60–70% sustained use." },
    ],
  },
];

export function MaxSecurityBoardPage() {
  const nodes = useMemo<Node[]>(
    () => [
      { id: "board", type: "board", position: { x: 0, y: 0 }, data: { width: 3400, groups: GROUPS }, draggable: false, selectable: false },
    ],
    [],
  );

  return (
    <div className="max-board" style={{ width: "100%", height: "100%", minHeight: 0, background: "#E5E5E5" }}>
      <style>{`
        .max-board .react-flow__pane { cursor: grab; }
        .max-board .react-flow__pane.dragging { cursor: grabbing; }
        .max-board .react-flow__node { cursor: default; }
      `}</style>
      <ReactFlow
        nodes={nodes}
        edges={[]}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.06, maxZoom: 1 }}
        minZoom={0.05}
        maxZoom={2.5}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        selectionOnDrag={false}
        panOnScroll
        panOnDrag
        zoomOnPinch
        zoomOnDoubleClick
      >
        <Background variant={BackgroundVariant.Dots} gap={26} size={1.3} color="#CFCFCF" />
      </ReactFlow>
    </div>
  );
}

export default MaxSecurityBoardPage;
